import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setToken, clearToken, getToken } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send cookies on every cross-origin request
axios.defaults.withCredentials = true;

const AuthContext = createContext({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    login: async () => {},
    register: async () => {},
    festivalJoin: async () => {},
    logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const applyToken = useCallback((t) => {
        setToken(t);
        if (t) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    }, []);

    // On mount: restore session from httpOnly cookie via /auth/me
    useEffect(() => {
        const initAuth = async () => {
            try {
                // Assertion arrives in the URL fragment so it is never sent in
                // an HTTP request, Referer header or frontend access log.
                const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
                const assertion = params.get('embed');
                if (window.location.pathname.startsWith('/embed/') && assertion) {
                    const res = await axios.post(`${API}/auth/embed`, { assertion });
                    if (res.data.allowed_path !== window.location.pathname) {
                        throw new Error('Embed access does not match this page');
                    }
                    const { access_token, user: userData } = res.data;
                    applyToken(access_token);
                    setUser(userData);
                    window.history.replaceState({}, document.title, window.location.pathname);
                    return;
                }
                const res = await axios.get(`${API}/auth/me`);
                const { access_token, ...userData } = res.data;
                applyToken(access_token);
                setUser(userData);
            } catch {
                // No valid session — user needs to log in
            } finally {
                setIsLoading(false);
            }
        };
        initAuth();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const login = async (email, password) => {
        const res = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, user: userData } = res.data;
        applyToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name) => {
        const res = await axios.post(`${API}/auth/register`, { email, password, name });
        const { access_token, user: userData, message } = res.data;
        if (access_token && access_token !== '') {
            applyToken(access_token);
            setUser(userData);
        }
        return { user: userData, message };
    };

    const festivalJoin = async (inviteCode, { firstName, lastName, company }) => {
        const res = await axios.post(`${API}/festival/signup/${inviteCode}`, {
            first_name: firstName, last_name: lastName, company,
        });
        const { access_token, user: userData } = res.data;
        applyToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`);
        } catch {
            // Ignore — clear locally regardless
        }
        applyToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            register,
            festivalJoin,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
